import { TagIcon } from '@heroicons/react/24/outline'
import { ChevronRightIcon, LockClosedIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import { getSmartPredictStates } from '../../constants/smart-predict'
import trackEvents from '../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { uuidSmall } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { ROLES } from '../../helpers/authorization-roles'
import {
  CUSTOM_STATUS_PREFIX,
  MAX_CUSTOM_STATUSES,
  PARTNER_TALENT_STATUSES_KEY,
  buildPartnerTalentStatuses,
  resolveLocalized
} from '../../helpers/talent-statuses'
import { useMutationAndUpdateQuery } from '../../hooks/use-mutation-and-update-query'
import OldButton from '../button'
import Divider from '../divider'
import SEO from '../seo'
import State from '../state'
import Badge from '../tailwind/Badge'
import SettingsContainer from './settings-container'
import ModalEditTalentStatus from './talent-statuses/modal-edit-talent-status'

const I18N_PREFIX = 'components.settings.talent_statuses.'

// Aus den Formularwerten eine Katalog-Definition bauen. Leere Übersetzungen
// werden nicht gespeichert, damit resolveLocalized sauber zurückfällt.
const toDefinition = (values, base) => {
  const label = { de: values.label_de.trim() }
  if (values.label_en.trim()) label.en = values.label_en.trim()

  const description = {}
  if (values.description_de.trim())
    description.de = values.description_de.trim()
  if (values.description_en.trim())
    description.en = values.description_en.trim()

  return { ...base, label, description, color: values.color }
}

/**
 * Eigene Talent-Status des Partners. Die 5 Aivy-Status bleiben fest, weil
 * Auswertung und Vorhersage auf ihnen aufbauen. Admins können zusätzliche
 * Status anlegen, bearbeiten und archivieren. Archivierte Status sind nicht
 * mehr wählbar, bleiben aber auf markierten Talenten sichtbar.
 * Alle anderen Rollen sehen den Stand schreibgeschützt.
 *
 * Aufbau wie die übrigen Settings-Bereiche: Untergruppen mit h4 und Divider,
 * Zeilen öffnen wie in der Teamverwaltung per Klick den Bearbeiten-Dialog.
 */
const TalentStatuses = () => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()
  const {
    activeSpace,
    cognitoUser: { username }
  } = useUserContext()

  const hasAdminRole = activeSpace.authorization_role.id === ROLES.ADMIN.id

  const [modal, setModal] = useState({ open: false, definition: null })
  const [modalKey, setModalKey] = useState(uuidSmall())

  useEffect(() => {
    trackEvent(trackEvents.OPEN_TALENT_STATUS_SETTINGS)
  }, [])

  const { mutate } = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const systemStates = getSmartPredictStates()
  const customStatuses = partner.talentStatuses || []
  const activeStatuses = customStatuses.filter(({ archived }) => !archived)
  const archivedStatuses = customStatuses.filter(({ archived }) => archived)
  const sortedStatuses = [...activeStatuses, ...archivedStatuses]
  const limitReached = activeStatuses.length >= MAX_CUSTOM_STATUSES

  // dashboard_settings immer frisch aus dem aktuellen partner lesen und die
  // bestehenden Keys mitnehmen, sonst löscht dieses Speichern die
  // Einstellungen anderer Bereiche.
  const persist = (items, eventName) =>
    new Promise((resolve, reject) => {
      const dashboardSettings = JSON.parse(
        partner.app_settings?.dashboard_settings ?? '{}'
      )
      const app_settings = { ...(partner.app_settings || {}) }
      app_settings.dashboard_settings = JSON.stringify({
        ...dashboardSettings,
        [PARTNER_TALENT_STATUSES_KEY]: buildPartnerTalentStatuses(items)
      })

      trackEvent(eventName)

      mutate(
        { input: { id: partner.id, app_settings } },
        {
          onSuccess: () => {
            success(t(I18N_PREFIX + 'success'))
            resolve()
          },
          onError: reject
        }
      )
    })

  const openCreate = () => {
    setModalKey(uuidSmall())
    setModal({ open: true, definition: null })
  }

  const openEdit = (definition) => {
    if (!hasAdminRole) return

    setModalKey(uuidSmall())
    setModal({ open: true, definition })
  }

  const closeModal = () => setModal((prev) => ({ ...prev, open: false }))

  const handleSubmit = async (values, existingId) => {
    if (existingId) {
      const items = customStatuses.map((definition) =>
        definition.id === existingId
          ? toDefinition(values, definition)
          : definition
      )

      await persist(items, trackEvents.UPDATE_TALENT_STATUS)
      return
    }

    const definition = toDefinition(values, {
      id: `${CUSTOM_STATUS_PREFIX}${uuidSmall()}`,
      archived: false,
      created_at: new Date().toISOString(),
      created_by: username
    })

    await persist(
      [...customStatuses, definition],
      trackEvents.CREATE_TALENT_STATUS
    )
  }

  const toggleArchived = async (definition) => {
    const items = customStatuses.map((item) =>
      item.id === definition.id ? { ...item, archived: !item.archived } : item
    )

    await persist(
      items,
      definition.archived
        ? trackEvents.RESTORE_TALENT_STATUS
        : trackEvents.ARCHIVE_TALENT_STATUS
    )
  }

  const renderCustomRow = (definition) => {
    const { label, description, color, archived } = definition
    const text = resolveLocalized(label, language)
    const descriptionText = resolveLocalized(description, language)
    const otherLanguage = language === 'de' ? 'en' : 'de'
    const otherLabel = label[otherLanguage]

    return (
      <div
        role='button'
        tabIndex={hasAdminRole ? 0 : -1}
        onClick={() => openEdit(definition)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openEdit(definition)
          }
        }}
        className={clsx(
          'group flex items-center justify-between gap-x-4 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50',
          hasAdminRole && 'cursor-pointer'
        )}
      >
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
            <span className={archived ? 'opacity-60' : undefined}>
              <State text={text} color={color} />
            </span>
            {archived && (
              <Badge text={t('common.archived')} color='gray' type='basic' />
            )}
            {descriptionText && (
              <span className='text-sm text-gray-500'>{descriptionText}</span>
            )}
          </div>
          {otherLabel && otherLabel !== text && (
            <p className='mt-0.5 text-xs leading-5 text-gray-500'>
              {t(I18N_PREFIX + `translation_${otherLanguage}`, {
                label: otherLabel
              })}
            </p>
          )}
        </div>
        {hasAdminRole && (
          <ChevronRightIcon
            className='h-5 w-5 flex-none text-gray-300 group-hover:text-gray-700'
            aria-hidden='true'
          />
        )}
      </div>
    )
  }

  return (
    <SettingsContainer
      title={t(I18N_PREFIX + 'title')}
      description={t(I18N_PREFIX + 'description')}
    >
      <SEO title={t('seo.titles.settings_talent_statuses')} />
      {!hasAdminRole && (
        <p className='text-sm text-gray-500'>
          {t(I18N_PREFIX + 'admin_only_hint')}
        </p>
      )}

      <div>
        <h4 className='text-sm font-medium leading-6 text-gray-900'>
          {t(I18N_PREFIX + 'system_group')}
        </h4>
        <p className='text-sm text-gray-500'>
          {t(I18N_PREFIX + 'system_group_hint')}
        </p>
        <div className='mt-4 flex flex-col gap-y-4'>
          {systemStates.map((state, index) => (
            <Fragment key={state.id}>
              <div className='flex items-center justify-between gap-x-4'>
                <div className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1'>
                  <State text={t(state.label)} color={state.color} />
                  {state.description && (
                    <span className='text-sm text-gray-500'>
                      {t(state.description)}
                    </span>
                  )}
                </div>
                <LockClosedIcon
                  className='h-5 w-5 flex-none text-aivyOrange'
                  aria-label={t(I18N_PREFIX + 'locked_label')}
                  title={t(I18N_PREFIX + 'locked_label')}
                />
              </div>
              {index < systemStates.length - 1 && <Divider marginY={0} />}
            </Fragment>
          ))}
        </div>
      </div>

      <Divider />

      <div
        className={hasAdminRole ? undefined : 'pointer-events-none opacity-60'}
        aria-disabled={!hasAdminRole || undefined}
      >
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <h4 className='text-sm font-medium leading-6 text-gray-900'>
              {t(I18N_PREFIX + 'custom_group')}
            </h4>
            <p className='max-w-2xl text-sm text-gray-500'>
              {t(I18N_PREFIX + 'custom_group_hint')}
            </p>
          </div>
          {hasAdminRole && customStatuses.length > 0 && !limitReached && (
            <OldButton
              appearance='plus'
              text={t(I18N_PREFIX + 'add_action')}
              onClick={openCreate}
              className='flex-shrink-0'
            />
          )}
        </div>

        {customStatuses.length === 0 ? (
          hasAdminRole ? (
            <button
              type='button'
              onClick={openCreate}
              className='relative mt-4 block w-full rounded-lg border-2 border-dashed border-gray-300 p-6 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            >
              <TagIcon
                className='mx-auto h-8 w-8 text-gray-400'
                aria-hidden='true'
              />
              <span className='mt-2 block text-sm font-medium italic text-gray-600'>
                {t(I18N_PREFIX + 'empty_title')}
              </span>
              <span className='mt-1 block text-xs text-gray-500'>
                {t(I18N_PREFIX + 'empty_description')}
              </span>
            </button>
          ) : (
            <p className='mt-4 text-sm italic text-gray-500'>
              {t(I18N_PREFIX + 'empty_title')}
            </p>
          )
        ) : (
          <div className='mt-4 flex flex-col gap-y-4'>
            {sortedStatuses.map((definition, index) => (
              <Fragment key={definition.id}>
                {renderCustomRow(definition)}
                {index < sortedStatuses.length - 1 && <Divider marginY={0} />}
              </Fragment>
            ))}
          </div>
        )}

        {limitReached && (
          <p className='mt-4 text-sm text-gray-500'>
            {t(I18N_PREFIX + 'limit_reached', { max: MAX_CUSTOM_STATUSES })}
          </p>
        )}
      </div>

      <ModalEditTalentStatus
        key={modalKey}
        open={modal.open}
        setOpen={closeModal}
        definition={modal.definition}
        existing={customStatuses}
        onSubmit={handleSubmit}
        onToggleArchived={toggleArchived}
        restoreDisabled={limitReached}
      />
    </SettingsContainer>
  )
}

export default TalentStatuses
